/**
 * Multi-tenant Supervisor Panel entry point.
 *
 * This module is deliberately kept thin — orchestration only:
 *   • Auth + logout + top-bar + tabs
 *   • Data fetching (tenants / stats / audit)
 *   • Tenant CRUD actions (create/edit/delete/suspend/extend/reset-pin)
 *   • Device-Trust escape hatch (unlock + reset-grace + new-device-code)
 *
 * Tabs, dialogs and helpers live under
 * ``src/components/supervisor/*`` (Feb 2026 refactor batch 1). Adding
 * a new tab? Create the file there and import it below.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supervisorApi } from "@/lib/SupervisorApi";
import {
  ShieldCheck, LogOut, RefreshCw, Building2, Activity, ListFilter, HardDrive, FlaskConical,
  CheckCircle2, XCircle, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import TenantsTab from "@/components/supervisor/TenantsTab";
import StatsTab from "@/components/supervisor/StatsTab";
import StorageTab from "@/components/supervisor/StorageTab";
import AuditTab from "@/components/supervisor/AuditTab";
import TestRunnerTab from "@/components/supervisor/TestRunnerTab";
import AgentTab from "@/components/supervisor/AgentTab";
import CreateTenantDialog from "@/components/supervisor/CreateTenantDialog";
import EditTenantDialog from "@/components/supervisor/EditTenantDialog";
import ResetPinDialog from "@/components/supervisor/ResetPinDialog";
import NewDeviceCodeDialog from "@/components/supervisor/NewDeviceCodeDialog";
import DeviceTrustToggleDialog from "@/components/supervisor/DeviceTrustToggleDialog";
import ForcePasswordChangeDialog from "@/components/supervisor/ForcePasswordChangeDialog";
import Security2FADialog from "@/components/supervisor/Security2FADialog";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("tenants"); // tenants | stats | audit
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null); // tenant object
  const [resetPinOpen, setResetPinOpen] = useState(null);
  const [newDeviceCodeTarget, setNewDeviceCodeTarget] = useState(null);
  const [deviceTrustTarget, setDeviceTrustTarget] = useState(null);
  const [forcePwChange, setForcePwChange] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [latestRun, setLatestRun] = useState(null); // latest test run for top-bar badge

  const refreshLatestRun = useCallback(async () => {
    try {
      const { data } = await supervisorApi.listRuns();
      setLatestRun((data.runs || [])[0] || null);
    } catch {
      /* silent — badge just won't update */
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("supervisor_token");
    localStorage.removeItem("supervisor_user");
    navigate("/supervisor/login", { replace: true });
  }, [navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, a] = await Promise.all([
        supervisorApi.listTenants(),
        supervisorApi.stats(),
        supervisorApi.audit(100),
      ]);
      setTenants(t.data);
      setStats(s.data);
      setAudit(a.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
      } else {
        toast.error(e?.response?.data?.detail || "Σφάλμα φόρτωσης");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    supervisorApi.me()
      .then(r => {
        setMe(r.data);
        if (r.data?.must_change_password) setForcePwChange(true);
      })
      .catch(() => logout());
    loadAll();
    refreshLatestRun();
  }, [loadAll, logout, refreshLatestRun]);

  // Poll latest test run every 20s for top-bar badge
  useEffect(() => {
    const interval = setInterval(refreshLatestRun, 20000);
    return () => clearInterval(interval);
  }, [refreshLatestRun]);

  // Auto-refresh tenants every 15s for live Local Agent status
  useEffect(() => {
    if (tab !== "tenants") return undefined;
    const interval = setInterval(() => {
      supervisorApi.listTenants()
        .then(r => setTenants(r.data))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [tab]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return tenants.filter(t => {
      if (statusFilter === "active" && t.suspended) return false;
      if (statusFilter === "suspended" && !t.suspended) return false;
      if (statusFilter === "trial" && !t.trial_end) return false;
      if (statusFilter === "agent_online" && t.agent_status !== "online") return false;
      if (statusFilter === "agent_offline" && t.agent_status === "online") return false;
      if (!q) return true;
      return (
        (t.tenant_code || "").toLowerCase().includes(q) ||
        (t.business_name || "").toLowerCase().includes(q) ||
        (t.business_afm || "").toLowerCase().includes(q) ||
        (t.business_phone || "").toLowerCase().includes(q)
      );
    });
  }, [tenants, filter, statusFilter]);

  // ----- Tenant actions -----
  const suspend = async (t, suspended) => {
    const reason = suspended ? (await promptDialog("Λόγος αναστολής:")) : null;
    if (suspended && !reason) return;
    try {
      await supervisorApi.suspendTenant(t.tenant_code, suspended, reason);
      toast.success(suspended ? "Αναστολή πελάτη" : "Επανενεργοποίηση");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  const removeTenant = async (t) => {
    // Confirmation 1 — soft warning + typed «DELETE» token
    const ok = (await confirmDialog(
      `⚠️ ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ του πελάτη «${t.business_name}» (${t.tenant_code}).\n\n` +
      `Θα διαγραφεί ΚΑΙ η βάση δεδομένων του — η ενέργεια ΔΕΝ αναιρείται.\n\n` +
      `Θα ζητηθούν άλλες 2 επιβεβαιώσεις.`
    ));
    if (!ok) return;
    // Confirmation 2 — typed keyword
    const typed = (await promptDialog(
      `Επιβεβαίωση 2/3\n\nΠληκτρολόγησε «DELETE» (κεφαλαία) για να διαγράψεις τον πελάτη «${t.tenant_code}»:`
    ));
    if (typed !== "DELETE") {
      toast.error("Ακυρώθηκε — δεν πληκτρολογήθηκε DELETE");
      return;
    }
    // Confirmation 3 — supervisor password re-verification
    const pwd = (await promptDialog(
      `Επιβεβαίωση 3/3\n\nΠληκτρολόγησε τον προσωπικό σου κωδικό διαχειριστή για επιβεβαίωση:`
    ));
    if (!pwd) {
      toast.error("Ακυρώθηκε — δεν δόθηκε κωδικός");
      return;
    }
    try {
      await supervisorApi.deleteTenant(t.tenant_code, pwd);
      toast.success(`Διαγράφηκε ο πελάτης «${t.business_name}»`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα διαγραφής");
    }
  };

  const extendSub = async (t) => {
    const daysStr = (await promptDialog(`Πόσες ημέρες να προστεθούν στη συνδρομή του «${t.tenant_code}»;`, "30"));
    const days = parseInt(daysStr || "0", 10);
    if (!days || days < 1) return;
    try {
      const r = await supervisorApi.extendSubscription(t.tenant_code, days);
      toast.success(`Συνδρομή έως ${new Date(r.data.subscription_end).toLocaleDateString("el-GR")}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  const clearLockouts = async (t) => {
    if (!(await confirmDialog(`Ξεκλείδωμα brute-force για «${t.tenant_code}»;\nΘα καθαριστούν όλες οι εγγραφές αποτυχημένων προσπαθειών login.`))) return;
    try {
      const r = await supervisorApi.clearLockouts(t.tenant_code);
      toast.success(`Ξεκλειδώθηκε (${r.data.cleared} εγγραφές διαγράφηκαν)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  const resetGrace = async (t) => {
    if (!(await confirmDialog(
      `Επανεκκίνηση της περιόδου χάριτος (24h) Device Trust για «${t.tenant_code}»;\n\n` +
      `Κάθε νέα συσκευή που θα συνδεθεί τις επόμενες 24 ώρες θα εγκριθεί αυτόματα ` +
      `μόλις μπει σωστά με το PIN της. Χρησιμοποίησε αυτή την επιλογή όταν ο πελάτης δεν έχει καμία ενεργή συσκευή για να δημιουργήσει κωδικό.`
    ))) return;
    try {
      const r = await supervisorApi.resetDeviceGrace(t.tenant_code);
      toast.success(`Ενεργοποιήθηκε ${r.data.grace_period_hours}ωρη περίοδος χάριτος`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  const forceLogout = async (t) => {
    if (!(await confirmDialog(
      `Force logout όλων των χειριστών για «${t.tenant_code}»;\n\n` +
      `Θα κλείσουν όλες οι ανοιχτές συνεδρίες. Χρησιμοποίησε αυτή την επιλογή ` +
      `όταν ένας χειριστής έμεινε "κολλημένος" ως συνδεδεμένος (π.χ. crash tablet) ` +
      `και δεν μπορεί να ξανασυνδεθεί λόγω του ελέγχου διπλής σύνδεσης.`
    ))) return;
    try {
      const r = await supervisorApi.forceLogout(t.tenant_code);
      toast.success(`Έκλεισαν ${r.data.closed} ενεργές συνεδρίες`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  if (loading && !tenants.length) {
    return (
      <div className="h-screen grid place-items-center bg-zinc-950 text-zinc-500">
        <RefreshCw className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Top bar */}
      <div className="sticky top-0 z-30 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/95 grid place-items-center overflow-hidden shadow-md">
              <img src="/ponte-pos-logo.jpg" alt="Ponte Pos" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">Ponte Pos — Supervisor</div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider font-bold">
                {me?.username || "—"} · {me?.role || "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CIStatusBadge run={latestRun} onClick={() => setTab("testing")} />
            <button
              onClick={() => setSecurityOpen(true)}
              data-testid="sup-security"
              className={`h-9 px-3 rounded-lg text-sm font-bold flex items-center gap-1.5 ${
                me?.totp_enabled
                  ? "bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-300"
                  : "bg-amber-900/40 hover:bg-amber-900/60 text-amber-300"
              }`}
              title={me?.totp_enabled ? "2FA ενεργό" : "2FA ανενεργό — συνιστάται"}
            >
              <ShieldCheck size={14} /> 2FA {me?.totp_enabled ? "ON" : "OFF"}
            </button>
            <button
              onClick={loadAll}
              data-testid="sup-refresh"
              className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold flex items-center gap-1.5"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="sup-logout"
              className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-rose-900 text-zinc-200 text-sm font-bold flex items-center gap-1.5"
            >
              <LogOut size={14} /> Έξοδος
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1 border-t border-zinc-900">
          {[
            { id: "tenants", label: "Πελάτες", icon: Building2 },
            { id: "stats", label: "Στατιστικά", icon: Activity },
            { id: "storage", label: "Αποθήκευση", icon: HardDrive },
            { id: "audit", label: "Audit Log", icon: ListFilter },
            { id: "agent", label: "Local Agent", icon: HardDrive },
            { id: "testing", label: "Testing", icon: FlaskConical },
          ].map(({ id, label, icon: Ico }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`sup-tab-${id}`}
              className={`h-11 px-4 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ${
                tab === id
                  ? "text-amber-400 border-amber-500"
                  : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
            >
              <Ico size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {tab === "tenants" && (
          <TenantsTab
            tenants={tenants}
            filtered={filtered}
            filter={filter} setFilter={setFilter}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            onCreate={() => setCreateOpen(true)}
            onSuspend={suspend}
            onDelete={removeTenant}
            onResetPin={(t) => setResetPinOpen(t)}
            onEdit={(t) => setEditOpen(t)}
            onExtendSub={extendSub}
            onClearLockouts={clearLockouts}
            onForceLogout={forceLogout}
            onNewDeviceCode={(t) => setNewDeviceCodeTarget(t)}
            onResetGrace={resetGrace}
            onToggleDeviceTrust={(t) => setDeviceTrustTarget(t)}
          />
        )}
        {tab === "stats" && <StatsTab stats={stats} />}
        {tab === "storage" && <StorageTab tenants={tenants} />}
        {tab === "audit" && <AuditTab rows={audit} />}
        {tab === "agent" && <AgentTab />}
        {tab === "testing" && <TestRunnerTab />}
      </div>

      {newDeviceCodeTarget && (
        <NewDeviceCodeDialog
          tenant={newDeviceCodeTarget}
          onClose={() => setNewDeviceCodeTarget(null)}
        />
      )}
      {deviceTrustTarget && (
        <DeviceTrustToggleDialog
          tenant={deviceTrustTarget}
          onClose={() => setDeviceTrustTarget(null)}
        />
      )}
      {createOpen && (
        <CreateTenantDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); loadAll(); }}
        />
      )}
      {forcePwChange && (
        <ForcePasswordChangeDialog
          onDone={() => { setForcePwChange(false); loadAll(); }}
          onLogout={logout}
        />
      )}
      {securityOpen && (
        <Security2FADialog
          totpEnabled={!!me?.totp_enabled}
          onClose={() => setSecurityOpen(false)}
          onChanged={() => {
            supervisorApi.me().then(r => setMe(r.data)).catch(() => {});
          }}
        />
      )}
      {editOpen && (
        <EditTenantDialog
          tenant={editOpen}
          onClose={() => setEditOpen(null)}
          onSaved={() => { setEditOpen(null); loadAll(); }}
        />
      )}
      {resetPinOpen && (
        <ResetPinDialog
          tenant={resetPinOpen}
          onClose={() => setResetPinOpen(null)}
          onDone={() => setResetPinOpen(null)}
        />
      )}
    </div>
  );
}

function CIStatusBadge({ run, onClick }) {
  if (!run) {
    return (
      <button
        onClick={onClick}
        data-testid="sup-ci-badge"
        title="Δεν υπάρχει test run ακόμη"
        className="h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 border border-zinc-700"
      >
        <FlaskConical size={13} /> CI: —
      </button>
    );
  }
  const s = run.summary || {};
  const isRunning = run.status === "running";
  const failed = (s.failed || 0) + (s.error || 0);
  let cls, Ico, label;
  if (isRunning) {
    cls = "bg-cyan-900/40 hover:bg-cyan-900/60 text-cyan-300 border-cyan-800";
    Ico = Loader2;
    label = "CI: Τρέχει";
  } else if (run.status === "error") {
    cls = "bg-rose-900/60 hover:bg-rose-900/80 text-rose-200 border-rose-700";
    Ico = AlertTriangle;
    label = `CI: ERROR`;
  } else if (failed > 0) {
    cls = "bg-rose-900/40 hover:bg-rose-900/60 text-rose-300 border-rose-800";
    Ico = XCircle;
    label = `CI: ${failed} FAIL`;
  } else {
    cls = "bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-300 border-emerald-800";
    Ico = CheckCircle2;
    label = `CI: ${s.passed || 0}/${s.total || 0}`;
  }
  const ago = run.finished_at || run.started_at;
  let title = `Latest run: ${run.id.slice(0, 8)}`;
  if (ago) {
    try {
      title += ` @ ${new Date(ago).toLocaleTimeString("el-GR")}`;
    } catch { /* ignore */ }
  }
  if (run.files?.length) title += ` · ${run.files.length} αρχεία`;
  return (
    <button
      onClick={onClick}
      data-testid="sup-ci-badge"
      title={title}
      className={`h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 border ${cls}`}
    >
      <Ico size={13} className={isRunning ? "animate-spin" : ""} /> {label}
    </button>
  );
}
